import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

// ── Phase status → dot style (matches blueprint phaseDots exactly) ────────────
function phaseDotStyle(st, color) {
  const c = color || '#3b7ef0';
  switch(st) {
    // Blueprint: Done is ALWAYS green #1db87a regardless of phase color
    case 'Done':        return { bg:'#1db87a',  fg:'#fff', shadow:'0 0 0 2px #1db87a44',          anim:'' };
    case 'In Progress': return { bg:c,          fg:'#fff', shadow:`0 0 0 3px ${c}33`,              anim:'crVoicePulse 1.3s ease-in-out infinite' };
    case 'Todo':        return { bg:'var(--bg2)',fg:c,     shadow:'none', border:`2px solid ${c}`, anim:'' };
    case 'Review':      return { bg:'#f7c34f',  fg:'#7a5800', shadow:'none',                       anim:'crVoicePulse 1.3s ease-in-out infinite' };
    case 'Blocked':     return { bg:'#e84545',  fg:'#fff', shadow:'none',                          anim:'' };
    case 'Rework':      return { bg:'#e87830',  fg:'#fff', shadow:'0 0 0 3px #e8783033',              anim:'crVoicePulse 1.3s ease-in-out infinite' };
    case 'Locked':
    default:            return { bg:'var(--bg3)',fg:'var(--text3)', shadow:'none', border:'1px solid var(--border)', anim:'' };
  }
}

const PHASE_ICONS = { Done:'✓', 'In Progress':'●', Todo:'○', Locked:'🔒', Review:'⏳', Blocked:'⛔', Rework:'↩' };
const MICRO_LABELS = {
  'In Progress': { text:'● working',       color:null },
  Review:        { text:'⏳ in approval',  color:'#9a6800' },
  Blocked:       { text:'⛔ on hold',       color:'#e84545' },
  Todo:          { text:'ready',           color:'var(--text3)' },
  Rework:        { text:'↩ rework',        color:'#e87830' },
};

// ── Phase dots strip (matches blueprint phaseDots render) ─────────────────────
function PhaseDots({ phStatuses, onOpenDetail }) {
  if (!phStatuses?.length) {
    return <span style={{fontSize:'0.72rem',color:'var(--text3)'}}>No phases — push to workflow first</span>;
  }
  return (
    <div style={{display:'flex',alignItems:'flex-start',gap:4,overflowX:'auto',paddingBottom:4,flexWrap:'nowrap'}}>
      {phStatuses.map(({p, t, st}, pi) => {
        const ds    = phaseDotStyle(st, p.color);
        const icon  = PHASE_ICONS[st] || '○';
        const micro = MICRO_LABELS[st];
        const pct   = st==='In Progress' ? (t?.progress || 0) : 0;
        return (
          <React.Fragment key={p.id||pi}>
            <div
              title={`${p.name} — ${st}${t?` (${pct}%)`:''}`}
              onClick={onOpenDetail}
              style={{display:'inline-flex',flexDirection:'column',alignItems:'center',gap:2,minWidth:52,maxWidth:72,cursor:'pointer'}}>
              <div style={{
                width:32,height:32,borderRadius:'50%',
                display:'flex',alignItems:'center',justifyContent:'center',
                fontSize:'0.7rem',fontWeight:700,flexShrink:0,
                background:ds.bg,color:ds.fg,
                boxShadow:ds.shadow,
                border:ds.border||'none',
                animation:ds.anim,
              }}>{icon}</div>
              <div style={{fontSize:'0.58rem',color:'var(--text3)',textAlign:'center',lineHeight:1.2,overflow:'hidden',maxWidth:70}}>
                {p.name.length>10 ? p.name.slice(0,10)+'…' : p.name}
              </div>
              {micro && <div style={{fontSize:'0.55rem',fontWeight:800,color:micro.color||(p.color||'var(--accent)'),whiteSpace:'nowrap'}}>{micro.text}</div>}
              {st==='In Progress' && (
                <div style={{width:32,height:3,background:'var(--border)',borderRadius:2,overflow:'hidden'}}>
                  <div style={{height:'100%',width:`${pct}%`,background:p.color||'var(--accent)'}} />
                </div>
              )}
            </div>
            {pi < phStatuses.length-1 && (
              <div style={{width:16,height:2,background:st==='Done'?'#1db87a':'var(--border)',marginTop:14,flexShrink:0}} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Live phase strips (current active phases) ─────────────────────────────────
function LivePhaseStrips({ liveX, woId }) {
  if (!liveX?.length) return null;
  return (
    <>
      {liveX.slice(0,4).map(({p, t, st}, i) => (
        <div key={i} style={{marginTop:8,padding:'6px 10px',background:`${p.color||'var(--accent)'}11`,borderRadius:6,fontSize:'0.75rem',display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
          <span style={{width:8,height:8,borderRadius:'50%',background:st==='Blocked'?'#e84545':(p.color||'var(--accent)'),flexShrink:0,
            animation:(st==='In Progress'||st==='Review')?'crVoicePulse 1.3s ease-in-out infinite':'none'}} />
          <span style={{fontWeight:700,color:p.color||'var(--accent)'}}>Current: {p.name}</span>
          <span style={{fontSize:'0.68rem',fontWeight:700,color:st==='Blocked'?'#e84545':st==='Review'?'#9a6800':'var(--text3)'}}>
            {st==='In Progress' ? '● working' : st==='Review' ? '⏳ in approval' : st==='Blocked' ? '⛔ on hold' : st==='Rework' ? '↩ rework needed' : '○ ready to start'}
          </span>
          {st==='Blocked' && t?.hold_reason && (
            <span style={{fontSize:'0.7rem',color:'#e84545'}}>— {t.hold_reason}</span>
          )}
          {t?.assignee && <span style={{fontSize:'0.72rem',color:'var(--text3)'}}>→ {t.assignee}</span>}
          {t?.deadline && (
            <span style={{fontFamily:'Space Mono,monospace',fontSize:'0.65rem',color:'var(--text3)',marginLeft:'auto'}}>
              Due: {(() => { const [dy,dm,dd]=(t.deadline||'').split('-'); return `${dd||'—'}-${dm||''}-${dy||''}`; })()}
            </span>
          )}
        </div>
      ))}
      {liveX.length > 4 && (
        <div style={{fontSize:'0.72rem',color:'var(--text3)',marginTop:4}}>
          +{liveX.length-4} more live phase{liveX.length-4>1?'s':''} — click the row for full detail
        </div>
      )}
    </>
  );
}


// ── Send Back Modal ───────────────────────────────────────────────────────────
function SendBackModal({ wo, phases, onClose, onSuccess }) {
  const [targetPhase, setTargetPhase] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState('');

  // Show phases the WO can be sent back to
  const phStatuses = wo.phase_statuses || [];
  const earlierPhases = (() => {
    if (phStatuses.length <= 1) return []; // Single-phase WO can't be sent back
    // Find the first non-Done phase (current active point)
    const currentIdx = phStatuses.findIndex(x => x.st !== 'Done');
    if (currentIdx === 0) return []; // Still at first phase — nothing to send back to
    if (currentIdx === -1) {
      // All phases are Done (posted/completed) — can send back to any phase
      return phStatuses.map(x => x.p);
    }
    // All phases before the current one that are Done
    return phStatuses.slice(0, currentIdx).map(x => x.p);
  })();

  const handleSubmit = async () => {
    if (!targetPhase) { setError('Please select a phase to send back to.'); return; }
    if (!reason.trim()) { setError('Please enter a reason for rework.'); return; }
    setError('');
    setSending(true);
    try {
      await api.post('/work-orders/' + wo.id + '/send-back', {
        target_phase: targetPhase,
        reason: reason.trim(),
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send back. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{position:'fixed',inset:0,zIndex:9500,display:'flex',alignItems:'center',justifyContent:'center',padding:24}}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{background:'rgba(0,0,0,0.5)',position:'absolute',inset:0}} onClick={onClose} />
      <div onClick={e => e.stopPropagation()} style={{position:'relative',background:'var(--bg2)',borderRadius:12,boxShadow:'0 8px 48px rgba(0,0,0,0.25)',width:440,maxWidth:'95vw',overflow:'hidden'}}>
        {/* Header */}
        <div style={{padding:'16px 20px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:10}}>
          <span style={{fontSize:'1.1rem'}}>↩️</span>
          <div style={{flex:1}}>
            <div style={{fontWeight:700,fontSize:'0.92rem',color:'var(--text1)'}}>Send Back for Rework</div>
            <div style={{fontSize:'0.72rem',color:'var(--text3)',marginTop:2}}>{wo.wo_number} — {wo.title}</div>
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',fontSize:'1.1rem',cursor:'pointer',color:'var(--text3)'}}>✕</button>
        </div>

        <div style={{padding:'18px 20px'}}>
          {error && <div style={{background:'#fee2e2',color:'#991b1b',padding:'8px 12px',borderRadius:6,fontSize:'0.78rem',marginBottom:14,border:'1px solid #fca5a5'}}>{error}</div>}

          {earlierPhases.length === 0 ? (
            <div style={{padding:20,textAlign:'center',color:'var(--text3)',fontSize:'0.82rem'}}>
              This work order cannot be sent back — it is still at the first phase.
            </div>
          ) : (
            <>
              <div style={{marginBottom:16}}>
                <label style={{display:'block',fontWeight:700,fontSize:'0.78rem',color:'var(--text2)',marginBottom:6}}>Send back to phase *</label>
                <select className="form-select" value={targetPhase} onChange={e => setTargetPhase(e.target.value)} style={{width:'100%'}}>
                  <option value="">Select a phase...</option>
                  {earlierPhases.map(p => (
                    <option key={p.id || p.name} value={p.name}>{p.name}</option>
                  ))}
                </select>
              </div>

              <div style={{marginBottom:16}}>
                <label style={{display:'block',fontWeight:700,fontSize:'0.78rem',color:'var(--text2)',marginBottom:6}}>Reason for rework *</label>
                <textarea
                  className="form-textarea"
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="Describe what needs to be reworked and why..."
                  style={{width:'100%',minHeight:90,resize:'vertical'}}
                />
              </div>

              <div style={{display:'flex',gap:8,justifyContent:'flex-end',paddingTop:4}}>
                <button onClick={onClose} className="btn btn-secondary" style={{fontSize:'0.78rem'}}>Cancel</button>
                <button onClick={handleSubmit} disabled={sending}
                  style={{background:'#e87830',color:'#fff',border:'none',borderRadius:6,padding:'8px 18px',fontWeight:700,fontSize:'0.78rem',cursor:sending?'not-allowed':'pointer',opacity:sending?0.6:1}}>
                  {sending ? 'Sending...' : '↩️ Send Back'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── WO Detail Slide-over Panel ────────────────────────────────────────────────
function WODetailPanel({ wo, onClose, onRefresh }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSendBack, setShowSendBack] = useState(false);

  useEffect(() => {
    api.get(`/work-orders/${wo.id}`)
      .then(r => setDetail(r.data.data || r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [wo.id]);

  const phStatuses = wo.phase_statuses || [];
  const tasks      = detail?.tasks || [];
  const [ey,em,ed] = (wo.event_date||'').split('-');
  const statusColor = { Active:'var(--accent)', Draft:'var(--text3)', 'On Hold':'#e87830', Done:'var(--green)', Cancelled:'var(--red)' };
  const sc = statusColor[wo.cached_status] || 'var(--text3)';

  return (
    <div style={{position:'fixed',inset:0,zIndex:8000,display:'flex',justifyContent:'flex-end'}}
      onClick={e => { if (e.target===e.currentTarget) onClose(); }}>
      <div style={{background:'rgba(0,0,0,0.4)',position:'absolute',inset:0}} onClick={onClose} />
      <div style={{position:'relative',width:420,maxWidth:'96vw',background:'var(--bg2)',borderLeft:'1px solid var(--border)',height:'100%',overflowY:'auto',boxShadow:'-8px 0 32px rgba(0,0,0,0.15)',display:'flex',flexDirection:'column'}}>

        {/* Header */}
        <div style={{padding:'18px 20px',borderBottom:'1px solid var(--border)',position:'sticky',top:0,background:'var(--bg2)',zIndex:1}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
            <div>
              <div style={{fontFamily:'Space Mono,monospace',fontWeight:700,color:'var(--accent)',fontSize:'0.78rem'}}>{wo.wo_number}</div>
              <div style={{fontWeight:700,fontSize:'1rem',margin:'2px 0',color:'var(--text1)'}}>{wo.title}</div>
              <div style={{display:'flex',gap:8,flexWrap:'wrap',marginTop:4}}>
                <span className="badge badge-blue">{wo.client?.name}</span>
                <span style={{fontSize:'0.72rem',fontWeight:700,color:sc}}>{wo.cached_status}</span>
                <span style={{fontFamily:'Space Mono,monospace',fontSize:'0.68rem',color:'var(--text3)'}}>📅 <b>Event:</b> {ed}-{em}-{ey}</span>
              </div>
            </div>
            <button onClick={onClose} style={{background:'none',border:'none',fontSize:'1.2rem',cursor:'pointer',color:'var(--text3)',flexShrink:0}}>✕</button>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:8,marginTop:8,flexWrap:'wrap'}}>
            {wo.campaign_type && <span style={{fontSize:'0.72rem',color:'var(--text3)'}}>🎯 {typeof wo.campaign_type==='object'?wo.campaign_type.name:wo.campaign_type}</span>}
            <button onClick={() => setShowSendBack(true)} style={{marginLeft:'auto',background:'#e8783016',color:'#e87830',border:'1px solid #e8783044',borderRadius:6,padding:'4px 12px',fontSize:'0.72rem',fontWeight:700,cursor:'pointer',display:'flex',alignItems:'center',gap:4}}>
              ↩️ Send Back
            </button>
          </div>
        </div>

        <div style={{padding:'18px 20px',flex:1}}>
          {/* Phase Timeline */}
          <div style={{fontWeight:700,fontSize:'0.85rem',marginBottom:12,color:'var(--text1)'}}>Phase Timeline</div>

          {loading ? (
            <div style={{textAlign:'center',padding:20,color:'var(--text3)',fontSize:'0.78rem'}}>Loading…</div>
          ) : phStatuses.length === 0 ? (
            <div style={{fontSize:'0.78rem',color:'var(--text3)'}}>No phase data available.</div>
          ) : phStatuses.map(({p, t, st}, idx) => {
            const pct     = t?.progress || 0;
            const [ty,tm,td] = (t?.deadline||'').split('-');
            const due     = td ? `${td}-${tm}-${ty}` : '—';
            const isCur   = ['In Progress','Todo','Review','Rework'].includes(st);
            const isDone  = st === 'Done';
            const phColor = p.color || 'var(--accent)';
            const dotIcon = {Done:'✅',Todo:'🔵','In Progress':'🔄',Review:'👁',Locked:'🔒',Blocked:'🔴',Rework:'↩️'}[st]||'⬜';
            return (
              <div key={p.id||idx} style={{display:'flex',gap:12,marginBottom:12,
                ...(isCur ? {padding:10,background:`${phColor}11`,borderRadius:8,borderLeft:`3px solid ${phColor}`} : {})}}>
                <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:0}}>
                  <div style={{width:28,height:28,borderRadius:'50%',background:st==='Locked'?'var(--bg3)':phColor,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'0.65rem',color:st==='Locked'?'var(--text3)':'#fff',fontWeight:700,flexShrink:0}}>
                    {idx+1}
                  </div>
                  {idx < phStatuses.length-1 && (
                    <div style={{width:2,flex:1,minHeight:12,background:isDone?(phColor||'var(--green)'):'var(--border)'}} />
                  )}
                </div>
                <div style={{flex:1,paddingTop:2}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                    <span style={{fontWeight:700,fontSize:'0.82rem',color:'var(--text1)'}}>{dotIcon} {p.name}</span>
                    <span style={{fontSize:'0.68rem',color:'var(--text3)'}}>{due}</span>
                    {t?.assignee && <span style={{fontSize:'0.68rem',color:'var(--text2)',marginLeft:'auto'}}>{t.assignee}</span>}
                  </div>
                  {st==='In Progress' && (
                    <div style={{marginTop:6,display:'flex',alignItems:'center',gap:8}}>
                      <div style={{flex:1,height:4,background:'var(--border)',borderRadius:2,overflow:'hidden'}}>
                        <div style={{height:'100%',width:`${pct}%`,background:phColor}} />
                      </div>
                      <span style={{fontSize:'0.68rem',fontWeight:700}}>{pct}%</span>
                    </div>
                  )}
                  {isCur && t && (
                    <div style={{marginTop:6,padding:'6px 10px',background:'var(--bg3)',borderRadius:6,fontSize:'0.72rem',color:'var(--text3)'}}>
                      ✏️ Progress updates automatically from module activity
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Tasks section */}
          {tasks.length > 0 && (
            <>
              <div style={{fontWeight:700,fontSize:'0.78rem',color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.06em',marginTop:20,marginBottom:8,paddingTop:14,borderTop:'1px solid var(--border)'}}>
                Workflow Tasks
              </div>
              {tasks.map(t => (
                <div key={t.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'4px 0',borderBottom:'1px solid var(--border)'}}>
                  <span style={{fontSize:'0.75rem',color:'var(--text2)',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.phase||t.title}</span>
                  <span style={{fontSize:'0.68rem',fontWeight:700,padding:'1px 7px',borderRadius:10,background:'var(--bg3)',color:'var(--text3)',whiteSpace:'nowrap',marginLeft:8}}>{t.status}</span>
                </div>
              ))}
            </>
          )}

          {/* Rework History */}
          {(wo.rework_history || []).length > 0 && (
            <>
              <div style={{fontWeight:700,fontSize:'0.78rem',color:'#e87830',textTransform:'uppercase',letterSpacing:'0.06em',marginTop:20,marginBottom:8,paddingTop:14,borderTop:'1px solid var(--border)'}}>
                ↩️ Rework History
              </div>
              {(wo.rework_history || []).map((rw, i) => (
                <div key={i} style={{padding:'8px 12px',background:'#e8783011',borderRadius:6,border:'1px solid #e8783033',marginBottom:6,fontSize:'0.75rem'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
                    <span style={{fontWeight:700,color:'#e87830'}}>→ {rw.target_phase}</span>
                    <span style={{fontSize:'0.65rem',color:'var(--text3)',fontFamily:'Space Mono,monospace'}}>
                      {rw.sent_back_at ? new Date(rw.sent_back_at).toLocaleDateString('en-GB') : ''}
                    </span>
                  </div>
                  <div style={{color:'var(--text2)'}}>{rw.reason}</div>
                  <div style={{fontSize:'0.68rem',color:'var(--text3)',marginTop:2}}>by {rw.sent_back_by}</div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Send Back Modal */}
        {showSendBack && (
          <SendBackModal
            wo={wo}
            phases={wo.phase_statuses?.map(ps => ps.p) || []}
            onClose={() => setShowSendBack(false)}
            onSuccess={() => { setShowSendBack(false); if (onRefresh) onRefresh(); onClose(); }}
          />
        )}
      </div>
    </div>
  );
}

// ── Main Pipeline Page ────────────────────────────────────────────────────────
export default function Pipeline() {
  const [wos,       setWos]       = useState([]);
  const [clients,   setClients]   = useState([]);
  const [wftypes,   setWftypes]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [wftF,      setWftF]      = useState('');
  const [clientF,   setClientF]   = useState('');
  const [statusF,   setStatusF]   = useState('');
  const [monthF,    setMonthF]    = useState('');
  const [woSearch,  setWoSearch]  = useState('');
  const [selected,  setSelected]  = useState(null);
  const [creativeModal, setCreativeModal] = useState(null); // {image_data, title}

  // Escape key closes creative lightbox
  useEffect(() => {
    if (!creativeModal) return;
    const h = e => { if (e.key === 'Escape') setCreativeModal(null); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [creativeModal]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [woRes, clRes, wfRes] = await Promise.all([
        api.get('/pipeline'),   // dedicated pipeline endpoint with phase statuses
        api.get('/clients'),
        api.get('/campaign-types'),
      ]);
      setWos(woRes.data.data || woRes.data || []);
      setClients(clRes.data.data || clRes.data || []);
      setWftypes(wfRes.data.data || wfRes.data || []);
    } catch(e) {
      // fallback to work-orders endpoint
      try {
        const [woRes, clRes, wfRes] = await Promise.all([
          api.get('/work-orders'),
          api.get('/clients'),
          api.get('/campaign-types'),
        ]);
        setWos(woRes.data.data || []);
        setClients(clRes.data.data || clRes.data || []);
        setWftypes(wfRes.data.data || wfRes.data || []);
      } catch(e2) { console.error(e2); }
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const months  = [...new Set(wos.map(wo => wo.event_date?.slice(0,7)).filter(Boolean))].sort();
  const statuses= [...new Set(wos.map(wo => wo.cached_status).filter(Boolean))].sort();

  const filtered = wos.filter(wo => {
    if (wftF    && String(wo.campaign_type_id||wo.campaign_type?.id) !== wftF) return false;
    if (clientF && String(wo.client?.id) !== clientF) return false;
    if (statusF && wo.cached_status !== statusF) return false;
    if (monthF  && !wo.event_date?.startsWith(monthF)) return false;
    if (woSearch) { const q=woSearch.toLowerCase(); if (!wo.wo_number?.toLowerCase().includes(q) && !wo.title?.toLowerCase().includes(q)) return false; }
    return true;
  }).sort((a,b) => (a.event_date||'') > (b.event_date||'') ? 1 : -1);

  const summary = `${filtered.length} work orders · ${filtered.filter(w=>w.cached_status==='Active').length} active`;
  const statusColors = { Active:'var(--accent)', Draft:'var(--text3)', 'On Hold':'#e87830', Done:'var(--green)', Cancelled:'var(--red)' };

  return (
    <div>
      <style>{`
        @keyframes crVoicePulse { 0%,100%{opacity:1} 50%{opacity:0.35} }
      `}</style>

      {/* Filters row */}
      <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap',alignItems:'center'}}>
        <select className="form-select" style={{width:180}} value={wftF} onChange={e=>setWftF(e.target.value)}>
          <option value="">All Workflow Types</option>
          {wftypes.map(ct => <option key={ct.id} value={ct.id}>{ct.name}</option>)}
        </select>
        <select className="form-select" style={{width:160}} value={clientF} onChange={e=>setClientF(e.target.value)}>
          <option value="">All Clients</option>
          {clients.filter(c=>c.enabled!==false).map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className="form-select" style={{width:140}} value={statusF} onChange={e=>setStatusF(e.target.value)}>
          <option value="">All Statuses</option>
          {statuses.map(s => <option key={s}>{s}</option>)}
        </select>
        <select className="form-select" style={{width:140}} value={monthF} onChange={e=>setMonthF(e.target.value)}>
          <option value="">All Months</option>
          {months.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <input className="form-select" type="search" placeholder="🔍 Search WO number or title…" value={woSearch} onChange={e=>setWoSearch(e.target.value)} style={{width:240,fontFamily:'inherit'}} />
        <span style={{fontSize:'0.75rem',color:'var(--text3)',fontFamily:'Space Mono,monospace',marginLeft:'auto'}}>{summary}</span>
      </div>

      {/* Pipeline board */}
      {loading ? (
        <div style={{textAlign:'center',padding:48,color:'var(--text3)'}}>Loading pipeline…</div>
      ) : filtered.length === 0 ? (
        <div style={{padding:40,textAlign:'center',color:'var(--text3)'}}>
          <div style={{fontSize:'2rem',marginBottom:12}}>📊</div>
          <div style={{fontWeight:700}}>No work orders found</div>
          <div style={{fontSize:'0.78rem',marginTop:6}}>Push calendar entries to workflow to see them here.</div>
        </div>
      ) : (
        <div>
          {filtered.map(wo => {
            const phStatuses = wo.phase_statuses || [];
            const sc = statusColors[wo.cached_status] || 'var(--border)';
            const [ey,em,ed] = (wo.event_date||'').split('-');

            // Find live phases (blueprint: liveX)
            let liveX = phStatuses.filter(x => ['In Progress','Review','Blocked','Rework'].includes(x.st));
            if (!liveX.length) {
              const ready = phStatuses.filter(x => x.st==='Todo');
              liveX = ready.length ? ready : [];
            }

            return (
              <div key={wo.id}
                onClick={() => setSelected(wo.id===selected?null:wo.id)}
                style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:'16px 18px',marginBottom:10,cursor:'pointer',borderLeft:`4px solid ${sc}`,transition:'box-shadow 0.15s'}}
                onMouseOver={e=>e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,0.08)'}
                onMouseOut={e=>e.currentTarget.style.boxShadow='none'}>

                {/* Row header */}
                <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:14,flexWrap:'wrap'}}>
                  <span style={{fontFamily:'Space Mono,monospace',fontWeight:700,color:'var(--accent)',fontSize:'0.78rem'}}>{wo.wo_number}</span>
                  <span className="badge badge-blue">{wo.client?.name}</span>
                  <span style={{fontWeight:700,color:'var(--text1)'}}>{wo.title}</span>
                  {wo.campaign_type && (
                    <span style={{fontSize:'0.68rem',fontWeight:700,color:'var(--text3)'}}>🎯 {typeof wo.campaign_type==='object'?wo.campaign_type.name:wo.campaign_type}</span>
                  )}
                  <span style={{marginLeft:'auto',fontSize:'0.72rem',fontWeight:700,color:sc}}>{wo.cached_status}</span>
                  {wo.creative?.image_data && wo.creative?.status==='Approved' && (
                    <span title="View approved creative" onClick={e=>{e.stopPropagation();setCreativeModal({image_data:wo.creative.image_data,title:wo.title,wo_number:wo.wo_number});}} style={{display:'inline-flex',alignItems:'center',gap:6,padding:'2px 8px 2px 4px',background:'#1db87a14',border:'1px solid #1db87a55',borderRadius:18,cursor:'pointer'}}>
                      <img src={wo.creative.image_data} alt="" style={{width:26,height:26,borderRadius:'50%',objectFit:'cover',border:'1px solid #fff',boxShadow:'0 0 0 1px #1db87a'}} />
                      <span style={{fontSize:'0.68rem',fontWeight:800,color:'#0f8050'}}>✅ View Approved</span>
                    </span>
                  )}
                  <span style={{fontFamily:'Space Mono,monospace',fontSize:'0.68rem',color:'var(--text3)'}}>📅 <b>Event:</b> {ed}-{em}-{ey}</span>
                </div>

                {/* Phase dots */}
                <PhaseDots phStatuses={phStatuses} onOpenDetail={() => setSelected(wo.id)} />

                {/* Live strips */}
                <LivePhaseStrips liveX={liveX} woId={wo.id} />
              </div>
            );
          })}
        </div>
      )}

      {/* Slide-over detail panel */}
      {selected && (() => {
        const wo = wos.find(w => w.id===selected);
        return wo ? <WODetailPanel wo={wo} onClose={() => setSelected(null)} onRefresh={load} /> : null;
      })()}

      {/* Approved creative lightbox */}
      {creativeModal && (
        <div onClick={()=>setCreativeModal(null)} style={{position:'fixed',inset:0,zIndex:9999,background:'rgba(0,0,0,0.78)',display:'flex',alignItems:'center',justifyContent:'center',padding:24}}>
          <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:16,boxShadow:'0 8px 48px rgba(0,0,0,0.5)',maxWidth:'90vw',maxHeight:'90vh',overflow:'hidden',display:'flex',flexDirection:'column'}}>
            {/* Header */}
            <div style={{display:'flex',alignItems:'center',gap:12,padding:'14px 20px',borderBottom:'1px solid #e5e7eb',background:'#f9fafb'}}>
              <span style={{background:'#dcfce7',color:'#166534',fontWeight:800,fontSize:'0.8rem',padding:'3px 10px',borderRadius:99,border:'1px solid #86efac'}}>✅ Approved Creative</span>
              <span style={{fontFamily:'Space Mono,monospace',fontWeight:700,color:'#3b7ef0',fontSize:'0.78rem'}}>{creativeModal.wo_number}</span>
              <span style={{fontWeight:700,color:'#111827',flex:1}}>{creativeModal.title}</span>
              <button onClick={()=>setCreativeModal(null)} style={{background:'none',border:'none',cursor:'pointer',fontSize:'1.4rem',color:'#6b7280',lineHeight:1,padding:'0 4px'}} title="Close (Esc)">×</button>
            </div>
            {/* Image */}
            <div style={{overflow:'auto',maxHeight:'calc(90vh - 60px)',display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
              <img src={creativeModal.image_data} alt="Approved creative" style={{maxWidth:'100%',maxHeight:'calc(90vh - 92px)',objectFit:'contain',borderRadius:8,boxShadow:'0 2px 16px rgba(0,0,0,0.18)'}} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}